import { normalizeHeadingElementTag } from "../ui/lib/textElementTag";
import type { TocHeadingItem } from "./collectTocHeadings";
import {
	DEFAULT_TOC_LEVELS,
	extractHeadingsFromHtmlMarkup,
} from "./collectTocHeadings";
import {
	plainTextFromHeadingHtml,
	resolveHeadingAnchorId,
} from "./slugifyHeadingId";

type HeadingLike = {
	codeType?: string;
	compiledCode?: string;
	content?: string;
	domId?: string;
	html?: boolean;
	props?: { tag?: string; content?: string };
	sourceCode?: string;
	staticHtml?: string;
	tag?: string;
	type?: string;
	children?: HeadingLike[];
};

function customCodeHeadingSources(node: HeadingLike): string[] {
	if (node.type !== "custom_code") {
		return [];
	}
	const sources: string[] = [];
	for (const raw of [node.sourceCode, node.staticHtml, node.compiledCode]) {
		if (typeof raw === "string" && raw.trim()) {
			sources.push(raw);
		}
	}
	return sources;
}

function headingPlainText(node: HeadingLike): string {
	const raw = node.content ?? "";
	if (node.html) {
		return plainTextFromHeadingHtml(raw);
	}
	return raw.replace(/<[^>]+>/g, "").trim();
}

function headingTag(node: HeadingLike): string {
	return normalizeHeadingElementTag(node.tag ?? node.props?.tag);
}

function headingLevel(node: HeadingLike): number {
	const level = Number.parseInt(headingTag(node).replace("h", ""), 10);
	return Number.isFinite(level) && level >= 1 && level <= 6 ? level : 2;
}

/** Normalize heading text for builder ↔ DOM matching when ids differ. */
export function normalizeTocHeadingMatchText(text: string): string {
	return text.replace(/\s+/g, " ").trim().toLowerCase();
}

/** Resolve which override key applies when DOM ids differ from builder ids. */
export function resolveTocLabelOverrideKey(
	item: TocHeadingItem,
	index: number,
	overrides: Record<string, string>,
	builderFallbackItems: TocHeadingItem[]
): string | null {
	if (overrides[item.id]?.trim()) {
		return item.id;
	}
	const byIndex = builderFallbackItems[index];
	if (byIndex && overrides[byIndex.id]?.trim()) {
		return byIndex.id;
	}
	const normalized = normalizeTocHeadingMatchText(item.text);
	for (const fallback of builderFallbackItems) {
		if (
			normalizeTocHeadingMatchText(fallback.text) === normalized &&
			overrides[fallback.id]?.trim()
		) {
			return fallback.id;
		}
	}
	return null;
}

/** Walk builder elements and assign heading ids the same way as runtime `nodeRenderer`. */
export function collectTocHeadingsFromElements(
	elements: HeadingLike[] | undefined,
	levels: number[] = DEFAULT_TOC_LEVELS
): TocHeadingItem[] {
	if (!elements?.length) {
		return [];
	}
	const used = new Set<string>();
	const items: TocHeadingItem[] = [];

	const walk = (nodes: HeadingLike[]) => {
		for (const node of nodes) {
			if (node.type === "heading") {
				const level = headingLevel(node);
				const text = headingPlainText(node);
				if (text && levels.includes(level)) {
					const id = resolveHeadingAnchorId({
						domId: node.domId,
						text,
						used,
					});
					items.push({ id, level, text });
				}
			} else if (node.type === "custom_code") {
				for (const html of customCodeHeadingSources(node)) {
					items.push(...extractHeadingsFromHtmlMarkup(html, levels, used));
				}
			}
			const children = node.children;
			if (Array.isArray(children) && children.length > 0) {
				walk(children);
			}
		}
	};

	walk(elements);
	return items;
}

/** Stable signature for stale AI label detection when headings change. */
export function computeTocHeadingSignature(headings: TocHeadingItem[]): string {
	return headings.map((h) => `${h.id}\t${h.level}\t${h.text}`).join("\n");
}

export function areTocLabelsStale(
	headings: TocHeadingItem[],
	storedSignature: string | undefined
): boolean {
	if (!storedSignature?.trim()) {
		return false;
	}
	return computeTocHeadingSignature(headings) !== storedSignature.trim();
}

export function countTocLabelOverrideMatches(
	items: TocHeadingItem[],
	overrides: Record<string, string> | null | undefined
): number {
	if (!overrides) {
		return 0;
	}
	return items.filter((item) => Boolean(overrides[item.id]?.trim())).length;
}

/**
 * Prefer live DOM scan for structure (levels, order). Builder fallback supplies
 * stable ids for AI labels via {@link applyTocLabelOverrides}, not as a replacement list.
 */
export function pickTocHeadingItemsForScan(options: {
	builderFallbackItems: TocHeadingItem[];
	domItems: TocHeadingItem[];
	labelOverrides?: Record<string, string> | null;
	useAiTocLabels?: boolean;
}): TocHeadingItem[] {
	const { builderFallbackItems, domItems, labelOverrides, useAiTocLabels } =
		options;
	if (domItems.length > 0) {
		return domItems;
	}
	if (
		useAiTocLabels === true &&
		labelOverrides &&
		builderFallbackItems.length > 0
	) {
		return builderFallbackItems;
	}
	return builderFallbackItems;
}

/** Apply entry-level AI overrides when enabled; unknown ids are ignored. */
export function applyTocLabelOverrides(
	items: TocHeadingItem[],
	options: {
		useAiTocLabels?: boolean;
		tocLabelOverrides?: Record<string, string> | null;
		/** Builder-derived headings used to map override keys when DOM ids differ. */
		builderFallbackItems?: TocHeadingItem[];
	}
): TocHeadingItem[] {
	if (options.useAiTocLabels !== true) {
		return items;
	}
	const overrides = options.tocLabelOverrides;
	if (!overrides || typeof overrides !== "object") {
		return items;
	}
	const builderFallbackItems = options.builderFallbackItems ?? [];
	return items.map((item, index) => {
		const overrideKey = resolveTocLabelOverrideKey(
			item,
			index,
			overrides,
			builderFallbackItems
		);
		if (!overrideKey) {
			return item;
		}
		const override = overrides[overrideKey]?.trim();
		if (!override) {
			return item;
		}
		return { ...item, text: override };
	});
}

export type PageTocLabelSettings = {
	useAiTocLabels?: boolean;
	tocLabelOverrides?: Record<string, string>;
	tocLabelSourceSignature?: string;
};

export function readPageTocLabelSettings(
	settings: Record<string, unknown> | undefined | null
): PageTocLabelSettings {
	if (!settings || typeof settings !== "object") {
		return {};
	}
	const useAiTocLabels = settings.useAiTocLabels === true;
	const rawOverrides = settings.tocLabelOverrides;
	const tocLabelOverrides =
		rawOverrides &&
		typeof rawOverrides === "object" &&
		!Array.isArray(rawOverrides)
			? Object.fromEntries(
					Object.entries(rawOverrides as Record<string, unknown>).filter(
						(entry): entry is [string, string] =>
							typeof entry[0] === "string" && typeof entry[1] === "string"
					)
				)
			: undefined;
	const signature =
		typeof settings.tocLabelSourceSignature === "string"
			? settings.tocLabelSourceSignature
			: undefined;
	return {
		...(useAiTocLabels ? { useAiTocLabels: true } : {}),
		...(tocLabelOverrides && Object.keys(tocLabelOverrides).length > 0
			? { tocLabelOverrides }
			: {}),
		...(signature ? { tocLabelSourceSignature: signature } : {}),
	};
}
